import { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/SidePanel";
import {
  Megaphone, Loader2, CheckCircle2, Clock, ImageOff, MessageCircle, Facebook, Instagram, Linkedin, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { HAZARD_LABEL, HAZARD_BASE } from "@/lib/hazard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_ICON = { whatsapp: MessageCircle, facebook: Facebook, instagram: Instagram, linkedin: Linkedin };

const STATUS_META = {
  generating: { label: "Fetching imagery…", cls: "text-amber-400 border-amber-400/40", Icon: Loader2, spin: true },
  image_ready: { label: "Image ready", cls: "text-atlas-cyan border-atlas-cyan/40", Icon: CheckCircle2 },
  generated_pending_delivery: { label: "Ready · delivery pending", cls: "text-atlas-orange border-atlas-orange/40", Icon: Clock },
  delivered: { label: "Delivered", cls: "text-emerald-400 border-emerald-400/40", Icon: CheckCircle2 },
  image_failed: { label: "Image failed", cls: "text-atlas-red border-atlas-red/40", Icon: AlertTriangle },
  no_image: { label: "Text only", cls: "text-atlas-secondary border-white/20", Icon: ImageOff },
};

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + "Z";
  } catch {
    return "";
  }
};

const ChannelChip = ({ c }) => {
  const Icon = CHANNEL_ICON[c.channel] || Megaphone;
  const sent = c.status === "sent";
  const failed = c.status === "failed";
  const tone = sent ? "text-emerald-400 border-emerald-400/40" : failed ? "text-atlas-red border-atlas-red/40" : "text-atlas-secondary/80 border-white/10";
  const label = sent ? "sent" : failed ? "failed" : "soon";
  return (
    <span
      className={`flex items-center gap-1 text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${tone}`}
      title={c.detail || c.status}
      data-testid={`channel-chip-${c.channel}`}
    >
      <Icon className="w-2.5 h-2.5" /> {c.channel} · {label}
    </span>
  );
};

const HasImage = (b) => Boolean(b.image_path || b.image_credit);

const BroadcastRow = ({ b }) => {
  const [imgError, setImgError] = useState(false);
  const sm = STATUS_META[b.status] || STATUS_META.generating;
  const color = HAZARD_BASE[b.hazard] || "#00F0FF";
  const critical = b.level === "critical";
  return (
    <div className="border-b border-white/10 p-3" data-testid={`broadcast-row-${b.uid}`}>
      <div className="w-full aspect-video bg-atlas-void/70 border border-white/10 mb-2 overflow-hidden flex items-center justify-center">
        {HasImage(b) && !imgError ? (
          <div className="relative w-full h-full">
          <img
            src={`${BACKEND}${b.image_endpoint}`}
            alt={`${b.hazard} broadcast`}
            className="w-full h-full object-contain"
            onError={() => setImgError(true)}
            data-testid={`broadcast-image-${b.uid}`}
          />
            {b.image_credit && (
              <span className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-atlas-void/80 text-[9px] font-mono text-atlas-secondary" data-testid={`broadcast-credit-${b.uid}`}>
                {b.image_credit}
              </span>
            )}
          </div>
        ) : b.status === "generating" ? (
          <div className="flex flex-col items-center gap-1.5 text-atlas-secondary/75">
            <Loader2 className="w-5 h-5 animate-spin text-atlas-cyan" />
            <span className="text-[9px] font-mono uppercase tracking-widest">Fetching satellite imagery…</span>
          </div>
        ) : (
          <ImageOff className="w-5 h-5 text-atlas-secondary/75" />
        )}
      </div>

      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
          <span className={`text-[9px] font-mono uppercase tracking-widest ${critical ? "text-atlas-red" : "text-atlas-orange"}`}>
            {critical ? "CRITICAL" : "WATCH"} · {HAZARD_LABEL[b.hazard] || b.hazard}
          </span>
        </div>
        <span className={`flex items-center gap-1 text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${sm.cls}`}>
          <sm.Icon className={`w-2.5 h-2.5 ${sm.spin ? "animate-spin" : ""}`} /> {sm.label}
        </span>
      </div>

      <div className="text-[12px] text-white/90 font-mono whitespace-pre-wrap leading-snug mb-2 bg-atlas-void/40 border-l-2 border-atlas-cyan/40 pl-2 py-1">
        {b.caption}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1">{(b.channels || []).map((c) => <ChannelChip key={c.channel} c={c} />)}</div>
        <span className="text-[9px] font-mono text-atlas-secondary/75 shrink-0 ml-2">{fmtTime(b.created_at)}</span>
      </div>
    </div>
  );
};

export const BroadcastsPanel = ({ open, onOpenChange }) => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.broadcasts(50), api.broadcastStatus()]);
      setBroadcasts(b);
      setStatus(s);
    } catch (e) {
      /* best-effort */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    const hasGenerating = broadcasts.some((b) => b.status === "generating");
    const id = setInterval(load, hasGenerating ? 6000 : 20000);
    return () => clearInterval(id);
  }, [open, load, broadcasts]);

  const waConfigured = status?.channels?.find((c) => c.channel === "whatsapp")?.configured;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className="w-full sm:max-w-md bg-atlas-surface border-l border-white/10 p-0 text-atlas-foreground"
        data-testid="broadcasts-panel"
      >
        <SheetHeader className="h-14 px-4 flex flex-row items-center justify-between border-b border-white/10 space-y-0">
          <SheetTitle className="flex items-center gap-2 text-white font-display">
            <Megaphone className="w-4 h-4 text-atlas-cyan" />
            Vigilo Earth Broadcasts
          </SheetTitle>
          {loading && <Loader2 className="w-4 h-4 text-atlas-cyan animate-spin" />}
        </SheetHeader>

        <div className="h-[calc(100vh-3.5rem)] overflow-y-auto">
          <div className="p-3 border-b border-white/10 bg-atlas-void/40" data-testid="broadcast-status">
            <p className="text-[11px] text-atlas-secondary leading-relaxed mb-2">
              Critical &amp; fire breakthroughs auto-post a real satellite snapshot (Sentinel-2 / VIIRS / MODIS) + caption. WhatsApp delivery is
              wired (activate with credentials); Facebook, Instagram &amp; LinkedIn are scaffolded and coming soon.
            </p>
            <div className="flex items-center gap-2 text-[10px] font-mono">
              <MessageCircle className={`w-3.5 h-3.5 ${waConfigured ? "text-emerald-400" : "text-atlas-orange"}`} />
              <span className="uppercase tracking-widest text-atlas-secondary/80">WhatsApp</span>
              <span className={waConfigured ? "text-emerald-400" : "text-atlas-orange"}>
                {waConfigured ? "connected" : "awaiting credentials"}
              </span>
              {status?.recipients != null && (
                <span className="text-atlas-secondary/75">· {status.recipients} recipient(s)</span>
              )}
            </div>
          </div>

          {broadcasts.length === 0 ? (
            <div className="flex items-center justify-center h-40 px-6 text-center">
              <span className="text-[11px] font-mono text-atlas-secondary/75 uppercase tracking-widest">
                No broadcasts yet. They appear automatically for critical & fire breakthroughs, or hit Broadcast on any alert.
              </span>
            </div>
          ) : (
            <div data-testid="broadcasts-list">
              {broadcasts.map((b) => <BroadcastRow key={b.uid} b={b} />)}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
